import type { Banner } from '@braze/web-sdk';
import { getGlobalVariables, isProduction, useBehavior } from '@libs/core';
import { TWidgetProps } from '@libs/types';
import React, { Activity, useEffect, useEffectEvent, useRef } from 'react';
import { TBrazeBanner } from '../../types/data';
import { TBrazeOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import styles from './BrazeBanner.module.css';

/**
 * For this widget we should set the Global variable in .env file with URL of chat script
 *  example:
 *  NX_PUBLIC_GLOBAL_brazeApiKey=ddf60d44-c684-485d-a08e-73ba5dcc2875
 *  NX_PUBLIC_GLOBAL_brazeEndpoint=sdk.fra-02.braze.eu
 */

type TBraze = typeof import('@braze/web-sdk');

export const BrazeBanner = ({ widget }: TWidgetProps<TBrazeBanner, TBrazeOption>) => {
    const { brazeApiKey, brazeEndpoint } = getGlobalVariables('brazeApiKey', 'brazeEndpoint');
    const bannerRef = useRef<HTMLDivElement>(null);
    const brazeRef = useRef<TBraze | null>(null);
    const { isHidden } = useBehavior(widget);

    const { placementId, userId } = widget.data || {};
    const { maxHeight } = widget.options || {};

    const initBraze = useEffectEvent((braze: TBraze) => {
        if (!braze.isInitialized()) {
            braze.initialize(brazeApiKey, {
                baseUrl: brazeEndpoint,
                enableLogging: !isProduction(),
                allowUserSuppliedJavascript: true,
            });
        }
    });

    const getBanner = useEffectEvent((braze: TBraze) => {
        if (braze.isInitialized() && userId && placementId) {
            braze.changeUser(userId);
            braze.openSession();

            braze.requestBannersRefresh([placementId]);
            braze.subscribeToBannersUpdates((banners: Record<string, Banner | undefined>) => {
                const banner = banners?.[placementId];
                if (!banner) {
                    return;
                }

                if (bannerRef.current) {
                    braze.insertBanner(banner, bannerRef.current);
                }
            });
        }
    });

    useEffect(() => {
        import(/* webpackChunkName: "braze-sdk" */ '@braze/web-sdk')
            .then((braze) => {
                brazeRef.current = braze;
                initBraze(braze);
                getBanner(braze);
            })
            .catch((error) => {
                console.error('Error initializing Braze SDK', error);
            });

        return () => {
            if (brazeRef.current) {
                brazeRef.current.destroy();
            }
        };
    }, []);

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div
                ref={bannerRef}
                data-test={widget.uid}
                style={getInlineStyles({ ...widget.options, height: `${maxHeight}px` })}
                className={styles.brazeBanner}
            />
        </Activity>
    );
};
